import React, { useId } from 'react';
import { Compass, X } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useHelp } from './HelpContext';
import type { TourDef } from './types';
import { usePublicSettings } from '../shared/settings/workspaceSettings';
import './help.css';

/**
 * Shown once, on a first visit (and again when the tour for this person's role
 * changes): a small card offering that tour. It never starts a tour by itself.
 * Dismissing it is remembered on the server, so it does not come back on another
 * device. Marked as a bottom bar so the quiet mode bubble moves above it.
 */
export default function WelcomeCard({ tour, changed }: { tour: TourDef; changed: boolean }) {
  const { t } = useI18n();
  const help = useHelp()!;
  const titleId = useId();
  // A super admin's own words for newcomers (Admin › Settings › General), under the tour offer.
  const note = usePublicSettings().welcome_note.trim();
  return (
    <section className="khelp-welcome" aria-labelledby={titleId} data-bottom-bar="">
      <span className="khelp-welcome__icon" aria-hidden="true"><Compass size={18} /></span>
      <div className="khelp-welcome__text">
        <h2 id={titleId} className="khelp-welcome__title">{changed ? t('help.welcome.changedTitle') : t('help.welcome.title')}</h2>
        <p className="khelp-welcome__body">{t(`help.welcome.body.${tour.id}`)}</p>
        {note && !changed && <p className="khelp-welcome__note">{note}</p>}
        <div className="khelp-welcome__actions">
          <button type="button" className="khelp-btn khelp-btn--primary" onClick={() => help.actions.startTour(tour.id)}>
            {t('help.takeTour')}
          </button>
          <button type="button" className="khelp-btn khelp-btn--quiet" onClick={help.actions.dismissWelcome}>
            {t('help.welcome.later')}
          </button>
        </div>
      </div>
      <button type="button" className="khelp-icon-btn khelp-icon-btn--small khelp-welcome__close" onClick={help.actions.dismissWelcome} aria-label={t('help.welcome.dismiss')}>
        <X size={16} aria-hidden="true" />
      </button>
    </section>
  );
}
